import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, GraduationCap, Receipt, Mail, PenTool,
  Image as ImageIcon, ImagePlus, UserCog, FileText, Settings,
  LogOut, Megaphone, BarChart3, Route as RouteIcon, Menu, X, KeyRound, Gift, ShieldCheck, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { LOGO_URL } from "./brand";

// Each entry maps to a permission key; owner sees everything.
// `perm: null` = always visible to any signed-in admin.
const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, perm: null },
  { to: "/admin/leads", label: "Leads", icon: Users, perm: "leads" },
  { to: "/admin/students", label: "Students", icon: GraduationCap, perm: "students" },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt, perm: "invoices" },
  { to: "/admin/vouchers", label: "Vouchers", icon: Gift, perm: "vouchers" },
  { to: "/admin/welcome", label: "Welcome Pack", icon: Mail, perm: "welcome_pack" },
  { to: "/admin/bulk-email", label: "Bulk Email", icon: Megaphone, perm: "bulk_email" },
  { to: "/admin/curriculum", label: "Curriculum", icon: FileText, perm: "curriculum" },
  { to: "/admin/blog", label: "Blog", icon: PenTool, perm: "blog" },
  { to: "/admin/social", label: "Photos & Social", icon: ImageIcon, perm: "social" },
  { to: "/admin/images", label: "Site Images", icon: ImagePlus, perm: "site_images" },
  { to: "/admin/team", label: "Tutors", icon: UserCog, perm: "team_tutors" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, perm: "analytics" },
  { to: "/admin/redirects", label: "Redirects", icon: RouteIcon, perm: "redirects" },
  { to: "/admin/settings", label: "Settings", icon: Settings, perm: "settings" },
  { to: "/admin/users", label: "Admin users", icon: KeyRound, perm: "__owner__" },
  { to: "/admin/security", label: "Security", icon: ShieldCheck, perm: null },
];

export default function AdminLayout() {
  const { user, logout, loading, can, endImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [returning, setReturning] = useState(false);

  const impersonating = !!user?.impersonated_by;

  const onReturn = async () => {
    setReturning(true);
    try {
      await endImpersonation();
      toast.success("Returned to your account");
      navigate("/admin/users");
    } catch (e) {
      toast.error("Could not restore your session — please sign in again");
    } finally {
      setReturning(false);
    }
  };

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [loading, user, navigate]);

  // Filter sidebar items by what this user is allowed to see.
  const visibleItems = useMemo(() => {
    if (!user) return [];
    return items.filter((it) => {
      if (it.perm === null) return true;
      if (it.perm === "__owner__") return user.role === "owner";
      return can ? can(it.perm) : true;
    });
  }, [user, can]);

  // Auto-close the drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  if (loading || !user) {
    return <div className="min-h-screen admin-bg flex items-center justify-center font-display text-[#5D2C8F]">Loading…</div>;
  }

  const SidebarContent = (
    <>
      <div className="flex items-center justify-between px-2 mb-6 lg:mb-8">
        <Link to="/admin" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="h-10 lg:h-12 w-auto object-contain" />
        </Link>
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden p-2 text-[#5D2C8F] rounded-md hover:bg-white"
          aria-label="Close menu"
          data-testid="admin-drawer-close"
        >
          <X size={22} />
        </button>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto -mx-1 px-1">
        {visibleItems.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? "bg-[#5D2C8F] text-white" : "text-[#5D2C8F] hover:bg-white"
              }`
            }
          >
            <it.icon size={17} /> {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto pt-4 border-t border-[#E5E0D8]">
        <div className="px-3 text-xs text-[#6E6074] mb-2 truncate">{user?.email}</div>
        <button onClick={logout} className="flex items-center gap-2 px-3 py-2 text-sm text-[#5D2C8F] hover:bg-white rounded-lg w-full" data-testid="admin-logout-btn">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className={`min-h-screen admin-bg flex flex-col lg:flex-row ${impersonating ? "pt-9" : ""}`} data-testid="admin-layout">
      {impersonating && (
        <div
          className="fixed top-0 inset-x-0 z-[60] bg-[#5D2C8F] text-white text-sm shadow-lg"
          data-testid="impersonation-banner"
        >
          <div className="max-w-screen-2xl mx-auto flex flex-wrap items-center gap-3 px-4 py-2">
            <Eye size={16} className="text-[#E8C37A] shrink-0" />
            <span className="flex-1 min-w-0">
              You are viewing as <strong data-testid="impersonation-banner-name">{user.name}</strong>
              <span className="text-white/70"> · signed in as {user.impersonated_by?.name || "Owner"}</span>
            </span>
            <button
              onClick={onReturn}
              disabled={returning}
              className="inline-flex items-center gap-1.5 bg-white text-[#5D2C8F] text-xs font-medium rounded-full px-3 py-1 hover:bg-[#F4EDDD] disabled:opacity-60"
              data-testid="impersonation-return-btn"
            >
              {returning ? "Returning…" : "Return to your account"}
            </button>
          </div>
        </div>
      )}
      {/* Mobile topbar */}
      <header className={`lg:hidden sticky ${impersonating ? "top-9" : "top-0"} z-30 bg-[#FAF6EE]/95 backdrop-blur-md border-b border-[#E5E0D8] flex items-center justify-between px-4 py-3`} data-testid="admin-topbar">
        <Link to="/admin" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="" className="h-8 w-auto object-contain" />
          <span className="font-display text-lg text-[#5D2C8F]">Admin</span>
        </Link>
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 text-[#5D2C8F] rounded-md hover:bg-white"
          aria-label="Open menu"
          data-testid="admin-menu-toggle"
        >
          <Menu size={22} />
        </button>
      </header>

      {/* Backdrop (mobile only) */}
      {mobileOpen && (
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          aria-label="Close menu"
          data-testid="admin-drawer-backdrop"
        />
      )}

      {/* Sidebar: drawer on mobile, static on desktop */}
      <aside
        className={`bg-[#FAF6EE] border-r border-[#E5E0D8] flex flex-col py-6 px-4
          fixed lg:sticky inset-y-0 left-0 z-50 lg:z-auto
          w-[82%] max-w-[300px] lg:w-64
          transition-transform duration-300 lg:transition-none
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          ${impersonating ? "lg:top-9 lg:h-[calc(100vh-2.25rem)]" : "lg:top-0 lg:h-screen"}`}
        data-testid="admin-sidebar"
      >
        {SidebarContent}
      </aside>

      <main className="flex-1 p-5 sm:p-6 lg:p-10 max-w-full overflow-x-hidden min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
